import { LazyImport, LazyModule } from 'owa-bundling';
import { Editor, EditorPlugin } from 'roosterjs-editor-core';
import { PluginEvent } from 'roosterjs-editor-types';
import PluginUtils from 'owa-editor/lib/store/schema/PluginUtils';
import isEditorPluginEx from 'owa-editor/lib/utils/isEditorPluginEx';
import LazyPlugin, { OnImportedCallback } from 'owa-editor/lib/store/schema/LazyPlugin';

export { LazyPlugin, OnImportedCallback };

// LazyPlugin type object type
export type LazyPluginClass<T extends EditorPlugin, P extends any[]> = new (
    ...arg: P
) => LazyPlugin<T>;

// Import trigger function type
export type ImportTrigger = (evt: PluginEvent, editor: Editor) => boolean;

// The real plugin type object type
export type PluginClass<T extends EditorPlugin, P extends any[]> = new (...args: P) => T;

/**
 * Create a Lazy editor plugin type
 * @param lazyModule lazyModule from which to get the PluginClass
 * @param getter function to get the PluginClass from the lazyModule once it is loaded
 * @param pluginName name of this plugin
 * @param importTrigger (optional) a function to check if the real plugin need to be loaded.
 * If null, the plugin will be loaded immediately when initialize
 * @param reusable (optional) Set to true to make the plugin instance reusable, so that after
 * the lazy plugin disposed and reinitialized, still reuse the same instance of real plugin
 * @example  (Suppose MyPlugin is the type of real plugin, myImportTrigger is the trigger function)
 *
 * import * as MyPluginModule from './MyPlugin';
 *
 * const lazyModule = new LazyModule(() =>
 *     import(
 *         \/* webpackChunkName: "MyPlugin" *\/ './MyPlugin'
 *     )
 * );
 *
 * export let LazyMyPluginClass = createLazyPlugin(
 *     lazyModule,
 *     m => m.default,
 *     myImportTrigger);
 *
 * When use this plugin:
 *    let myPlugin: LazyMyPlugin = new LazyMyPluginClass(...);
 *    let myRealPlugin = await myPlugin.getPlugin();
 */
export default function createLazyPlugin<
    TPlugin extends EditorPlugin,
    TParam extends any[],
    TModule
>(
    lazyModule: LazyModule<TModule>,
    getter: (module: TModule) => PluginClass<TPlugin, TParam>,
    pluginName: string,
    importTrigger?: ImportTrigger,
    reusable?: boolean
): LazyPluginClass<TPlugin, TParam> {
    return class implements LazyPlugin<TPlugin> {
        private pluginInstance: TPlugin;
        private editor: Editor;
        private args: TParam;
        private onImportedCallbacks: OnImportedCallback<TPlugin>[] = [];
        private importPluginPromise: Promise<TPlugin>;
        private pluginUtils: PluginUtils;
        private pluginName: string;

        constructor(...args: TParam) {
            this.args = args;
            this.pluginName = pluginName;
        }

        initialize(editor: Editor) {
            this.editor = editor;

            if (this.pluginInstance) {
                this.initializeAndCallback();
            } else if (!importTrigger) {
                this.internalImport();
            }
        }

        dispose() {
            if (this.pluginInstance) {
                this.pluginInstance.dispose();

                if (!reusable) {
                    this.pluginInstance = null;
                }
            }
            this.editor = null;
            this.importPluginPromise = null;

            // reset to empty array in case this plugin is reused
            this.onImportedCallbacks = [];
        }

        getName() {
            return this.isImported() ? this.pluginInstance.getName() : this.pluginName;
        }

        setPluginUtils(pluginUtils: PluginUtils) {
            this.pluginUtils = pluginUtils;
            if (isEditorPluginEx(this.pluginInstance)) {
                this.pluginInstance.setPluginUtils(this.pluginUtils);
            }
        }

        onPluginEvent(event: PluginEvent) {
            if (this.pluginInstance) {
                if (this.pluginInstance.onPluginEvent) {
                    this.pluginInstance.onPluginEvent(event);
                }
            } else if (importTrigger && importTrigger(event, this.editor)) {
                this.internalImport(event);
            }
        }

        willHandleEventExclusively(event: PluginEvent) {
            if (this.pluginInstance && this.pluginInstance.willHandleEventExclusively) {
                return this.pluginInstance.willHandleEventExclusively(event);
            } else {
                return false;
            }
        }

        getPlugin(): Promise<TPlugin> {
            if (this.pluginInstance) {
                return Promise.resolve(this.pluginInstance);
            } else {
                return this.internalImport();
            }
        }

        isImported(): boolean {
            return !!this.pluginInstance;
        }

        addOnImportedCallback(callback: OnImportedCallback<TPlugin>) {
            if (this.isImported() && this.editor) {
                callback(this.pluginInstance);
            } else if (this.onImportedCallbacks) {
                this.onImportedCallbacks.push(callback);
            }
        }

        private initializeAndCallback() {
            this.pluginInstance.initialize(this.editor);
            this.onImportedCallbacks.forEach(callback => callback(this.pluginInstance));
        }

        private internalImport(event?: PluginEvent): Promise<TPlugin> {
            if (!this.importPluginPromise) {
                this.importPluginPromise = new LazyImport(lazyModule, getter)
                    .import()
                    .then(PluginClass => {
                        this.pluginInstance = new PluginClass(...this.args);
                        this.setPluginUtils(this.pluginUtils);

                        if (this.editor) {
                            this.initializeAndCallback();

                            if (event && this.pluginInstance.onPluginEvent) {
                                this.pluginInstance.onPluginEvent(event);
                            }
                        }

                        return this.pluginInstance;
                    });
            }

            return this.importPluginPromise;
        }
    };
}
